/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Collapse, Select } from 'antd';
import React, {
  useEffect, useMemo, useRef, useState
} from 'react';
import { set } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Typography } from 'tfc-components';

import FreeTrial from './FreeTrialModal';
import PricingList from './PricingList';
import PricingTable from './PricingTable';
import useIsMobile from './helpers';

import Icon, { IconName } from 'components/atoms/Icon';
import Section from 'components/organisms/Section';
import FaqSection, { IFaqCategory } from 'pages/Home/FaqSection';
import ModalRegister from 'pages/Home/ModalRegister';
import { getPackageCompareService } from 'services/systems';
import { ROUTE_PATHS } from 'utils/constants';
import mapModifiers from 'utils/functions';

interface IAddOn {
  key: string;
  icon: IconName;
  title: string;
  description: string;
}

interface ICardDescription {
  key: string;
  title: string;
  description: string[];
  price: number;
  period: string;
  isFree?: boolean;
  info?: string;
}

export const pricingPlans1 = [
  {
    id: 1,
    name: 'Free trial',
    price: 100000,
    priceOff: 0,
    period: '7 day trial',
    suitable: 'For individuals',
    features: ['1 Chatbot', '100 Sessions', 'Knowledge base: 3 files, 1 links', '30 MB Storage'],
    addOnPackage: ['Chatbot', 'Knowledge base', 'Sessions'],
    buttonText: 'Get Started',
    highlighted: false,
    isFree: true
  },
  {
    id: 2,
    name: 'Starter',
    price: 619000,
    priceOff: 0,
    period: 'per month',
    suitable: 'For small teams',
    features: ['2 Chatbot', '1.000 Sessions', 'Knowledge base: 100 files, 5 links', '512 MB Storage', 'Reporting & Analytics'],
    addOnPackage: ['Chatbot', 'Knowledge base', 'Sessions'],
    buttonText: 'Buy Now',
    highlighted: false,
    isFree: false
  },
  {
    id: 3,
    name: 'Plus',
    price: 8900000,
    priceOff: 0,
    period: 'per month',
    suitable: 'For small teams',
    features: ['5 Chatbot', '2.000 Sessions', 'Knowledge base: 1000 files, 50 links', '2 GB Storage', 'Reporting & Analytics'],
    addOnPackage: ['Chatbot', 'Knowledge base', 'Sessions'],
    buttonText: 'Buy Now',
    highlighted: false,
    isFree: false
  },
  {
    id: 4,
    name: 'Business',
    price: 14900000,
    priceOff: 0,
    period: 'per month',
    suitable: 'For growing businesses',
    features: ['10 Chatbot', '5.000 Sessions', 'Knowledge base: 2000 files, 500 links', '5GB Storage',
      'Advanced integration (Sharepoint, Google Drive, Confluence, Internal Database)', 'AI Action', 'Voice chat AI (Speech to text)',
      'Reporting & Analytics'],
    addOnPackage: ['Chatbot', 'Knowledge base', 'Sessions'],
    buttonText: 'Buy Now',
    highlighted: true,
    isFree: false
  },
  {
    id: 5,
    name: 'Enterprise',
    price: 0,
    priceOff: undefined,
    period: undefined,
    suitable: 'For organizations with advanced support, security, and control.',
    features: ['Custom Chatbot limit', 'Custom Sessions limit', 'Knowledge base: Custom limit', 'Custom Storage limit',
      'Advanced integration (Sharepoint, Google Drive, Confluence, Internal Database)', 'AI Action',
      'Voice chat AI (Speech to text)', 'Reporting & Analytics'],
    addOnPackage: ['Chatbot', 'Knowledge base', 'Sessions'],
    buttonText: 'Contact Sales',
    highlighted: false,
    isFree: false
  },
];

export const pricingPlans2 = [
  {
    id: 1,
    name: 'Free trial',
    price: 0,
    priceOff: 0,
    period: '7 day trial',
    suitable: 'For individuals',
    features: ['1 Chatbot', '100 Sessions', 'Knowledge base: 3 files, 1 links', '30 MB Storage'],
    addOnPackage: ['Chatbot', 'Knowledge base', 'Sessions'],
    buttonText: 'Get Started',
    highlighted: false,
    isFree: true
  },
  {
    id: 2,
    name: 'Starter',
    price: 449000,
    priceOff: 0,
    period: 'per month, billed annually',
    suitable: 'For small teams',
    features: ['2 Chatbot', '1.000 Sessions', 'Knowledge base: 100 files, 5 links', '512 MB Storage', 'Reporting & Analytics'],
    addOnPackage: ['Chatbot', 'Knowledge base', 'Sessions'],
    buttonText: 'Buy Now',
    highlighted: false,
    isFree: false
  },
  {
    id: 3,
    name: 'Plus',
    price: 5900000,
    priceOff: 0,
    period: 'per month, billed annually',
    suitable: 'For small teams',
    features: ['5 Chatbot', '2.000 Sessions', 'Knowledge base: 1000 files, 50 links', '2 GB Storage', 'Reporting & Analytics'],
    addOnPackage: ['Chatbot', 'Knowledge base', 'Sessions'],
    buttonText: 'Buy Now',
    highlighted: false,
    isFree: false
  },
  {
    id: 4,
    name: 'Business',
    price: 10900000,
    priceOff: 0,
    period: 'per month, billed annually',
    suitable: 'For growing businesses',
    features: ['10 Chatbot', '5.000 Sessions', 'Knowledge base: 2000 files, 500 links', '5GB Storage',
      'Advanced integration (Sharepoint, Google Drive, Confluence, Internal Database)', 'AI Action', 'Voice chat AI (Speech to text)',
      'Reporting & Analytics'],
    addOnPackage: ['Chatbot', 'Knowledge base', 'Sessions'],
    buttonText: 'Buy Now',
    highlighted: true,
    isFree: false
  },
  {
    id: 5,
    name: 'Enterprise',
    price: 0,
    priceOff: undefined,
    period: undefined,
    suitable: 'For organizations with advanced support, security, and control.',
    features: ['Custom Chatbot limit', 'Custom Sessions limit', 'Knowledge base: Custom limit', 'Custom Storage limit',
      'Advanced integration (Sharepoint, Google Drive, Confluence, Internal Database)', 'AI Action',
      'Voice chat AI (Speech to text)', 'Reporting & Analytics'],
    addOnPackage: ['Chatbot', 'Knowledge base', 'Sessions'],
    buttonText: 'Contact Sales',
    highlighted: false,
    isFree: false
  },
];

const faqData: IFaqCategory[] = [
  {
    title: 'Common FAQ',
    faqs: [
      {
        title: 'What’s included in the Free plan?',
        description: [
          'The Free plan includes 1 chatbot, 100 sessions, 3 files + 1 link in the Knowledge Base, and 30MB of storage.',
          'This is a 7-day trial plan.'
        ],
      },
      {
        title: 'Can I upgrade or downgrade my plan?',
        description: [
          'Yes, you can upgrade or downgrade your plan at any time.',
          'Changes will take effect from the next billing cycle.'
        ],
      },
      {
        title: 'What payment methods are available?',
        description: [
          'Ask2Auto supports payments via credit/debit cards (Visa, MasterCard) or bank transfer under contract (for Enterprise plans).'
        ],
      },
      {
        title: 'Does Ask2Auto provide VAT invoices?',
        description: [
          "Yes. After payment, you can request a VAT invoice by filling in your company details under the 'Billing Info' section."
        ],
      },
    ]
  },
  {
    title: 'Additional FAQ',
    faqs: [
      {
        title: "What is the 'Chatbots' add-on used for?",
        description: [
          'This add-on allows you to create additional chatbots beyond the number included in your main plan.',
          'Each extra chatbot is billed separately on a monthly basis.'
        ],
      },
      {
        title: "What are the benefits of the 'Knowledge Base' add-on?",
        description: [
          'It allows you to expand the number of files, links, and storage capacity so your chatbot can learn more content and provide more accurate responses.'
        ],
      },
      {
        title: "How does the 'Sessions' add-on work?",
        description: [
          'Each session represents one interaction between a user and a chatbot.',
          'You can purchase additional sessions when you exceed your main plan limit, which is especially useful during marketing campaigns or peak seasons.'
        ],
      },
      {
        title: 'Can I purchase an add-on without a main plan?',
        description: [
          'No. You need to have a main plan (Starter, Business, or Enterprise) in order to purchase any add-ons.'
        ],
      }
    ]
  }
];

const cardAddOn: IAddOn[] = [
  {
    key: 'chatbot',
    icon: 'robotAgentGray',
    title: 'Chatbots',
    description: 'Expand the number of chatbots you can create and manage on the Ask2Auto Portal.',
  },
  {
    key: 'knowledge',
    icon: 'knowledge',
    title: 'Knowledge base',
    description: 'Add more content to power smarter chatbots',
  },
  {
    key: 'sessions',
    icon: 'monitorGray',
    title: 'Sessions',
    description: 'Get more interactions with your users',
  },
];

const cardDescription: ICardDescription[] = [
  {
    key: 'chatbot',
    title: 'Increase chatbot quota to handle more use cases',
    description: [
      'Build dedicated chatbots for each department (Support, HR, Technical, Training...)',
      'Assign distinct content and permissions per chatbot'
    ],
    price: 10000,
    period: 'per chatbot/year'
  },
  {
    key: 'knowledge',
    title: 'Purchase extra files, links, and storage to train your chatbot with rich, up-to-date content.',
    description: [
      'Add more files and overall storage capacity',
      'Supports multiple formats: PDFs, docs, websites...',
      'Keep chatbot knowledge up-to-date with ease'
    ],
    price: 10000,
    period: 'per package/year',
    info: 'Each package includes x file, x websites and x MB of storage'
  },
  {
    key: 'sessions',
    title: 'Buy additional sessions to enable more user interactions with your chatbot each duration.',
    description: [
      'Scale sessions for campaigns or seasonal spikes',
      'Prevent session limits from disrupting user flow',
      'Perfect for fast-scaling teams or customer-facing use cases'
    ],
    price: 10000,
    period: 'per x session/year'
  }
];

const addOnPackagesChatBot = [
  { name: 'Starter', count: '1', price: '189.000 VND' },
  { name: 'Plus', count: '1', price: '174.000 VND' },
  { name: 'Business', count: '1', price: '159.000 VND' },
].map((item, index) => ({
  ...item,
  id: `addon-chatbot-${index}-${item.name}`,
}));

const addOnPackagesKnowledge = [
  { name: 'Starter', count: '15', price: '125.000 VND' },
  { name: 'Plus', count: '15', price: '115.000 VND' },
  { name: 'Business', count: '15', price: '106.000 VND' },
].map((item, index) => ({
  ...item,
  id: `addon-${index}-${item.name}`,
}));

const knowledgeNumberOfWeb = [
  { name: 'Starter', count: '1', price: '125.000 VND' },
  { name: 'Plus', count: '1', price: '115.000 VND' },
  { name: 'Business', count: '1', price: '106.000 VND' },
].map((item, index) => ({
  ...item,
  id: `web-${index}-${item.name}`,
}));

const mBStorageKnowledge = [
  { name: 'Starter', count: '100 MB', price: '125.000 VND' },
  { name: 'Plus', count: '100 MB', price: '115.000 VND' },
  { name: 'Business', count: '100 MB', price: '106.000 VND' },
].map((item, index) => ({
  ...item,
  id: `storage-${index}-${item.name}`,
}));

const addOnPackagesSessions = [
  { name: 'Starter', count: '1000', price: '300.000 VND' },
  { name: 'Plus', count: '1000', price: '290.000 VND' },
  { name: 'Business', count: '1000', price: '280.000 VND' },
].map((item, index) => ({
  ...item,
  id: `addon-session-${index}-${item.name}`,
}));

const PricingPlan: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'monthly' | 'annually'>('monthly');
  const [activeTabAddOn, setActiveTabAddOn] = useState<'monthly' | 'annually'>('monthly');
  const [selectedAddOn, setSelectedAddOn] = useState<string | null>('chatbot');
  const { data } = useQuery(['getPackageCompareService'], () => getPackageCompareService());
  const pricingPlan = useMemo(() => (activeTab === 'monthly' ? pricingPlans1 : pricingPlans2), [activeTab]);
  const [showFloatingBlock, setShowFloatingBlock] = useState(false);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState<number>(0);
  const compareSectionRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenFreeModal, setIsOpenFreeModal] = useState(false);

  const isMobile = useIsMobile();

  useEffect(() => {
    const handleScroll = () => {
      if (tableContainerRef.current) {
        const { top, height } = tableContainerRef.current.getBoundingClientRect();
        const { scrollY } = window;
        const sectionTop = scrollY + top;
        const sectionBottom = sectionTop + height;
        if (!isMobile) {
          setShowFloatingBlock(scrollY >= sectionTop - 300 && scrollY < (sectionBottom - 300));
        } else {
          setShowFloatingBlock(scrollY >= sectionTop - 50 && scrollY < (sectionBottom - 40));
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleOpenModal = (planName: string) => {
    if (planName !== 'Enterprise' && planName !== 'Free trial') {
      navigate(`${ROUTE_PATHS.CHECKOUT}?planName=${planName}`);
    } else if (planName === 'Free trial') {
      setIsOpenFreeModal(true);
    } else {
      setIsOpen(true);
    }
  };

  return (
    <Section className="p-pricingPlan">
      <Section.Title sectionName="Pricing" className="p-pricingPlan_titleSection">
        One tool for your whole
        {isMobile && <br />}
        company.
        {!isMobile && <br />}
        {' '}
        Free for
        {' '}
        {' '}
        {isMobile && <br />}
        teams to try.
      </Section.Title>
      <div className={mapModifiers('p-pricingPlan_tabs', activeTab === 'annually' && 'right')}>
        {['monthly', 'annually'].map((tab) => (
          <Button
            key={tab}
            extendClasses={`p-pricingPlan_tab p-pricingPlan_tab-${activeTab === tab ? 'active' : ''} ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab as 'monthly' | 'annually')}
          >
            {tab === 'monthly' && <Typography.Text fontweight="600">Monthly</Typography.Text>}
            {tab === 'annually' && <Typography.Text fontweight="600">Annually</Typography.Text>}
          </Button>
        ))}
      </div>
      <div className="p-pricingPlan_descriptionContainer">
        <Typography.Text fontweight="600" extendClasses="p-pricingPlan_description fs-14x20">Price in VND</Typography.Text>
      </div>
      <PricingList plans={pricingPlan} handleOpenModal={handleOpenModal} />
      <div
        className="p-pricingPlan_tableContainer"
        ref={tableContainerRef}
      >
        <div className="p-pricingPlan_table_compare">
          <Typography.Text fontweight="600" extendClasses="p-pricingPlan_tableCompareTitle fs-18x22">Compare packages in detail</Typography.Text>
          <Icon iconName="caretDownActive" size="32" />
        </div>
        {
          showFloatingBlock && isMobile && (
            <div
              className="p-pricingPlan_floatingBlockMobile"
              role="region"
              aria-label="Pricing comparison action block"
            >
              <Section
                className="p-pricingPlan_floatingBlockSectionMobile"
                defaultValue={0}
              >
                <div ref={compareSectionRef} className="p-pricingPlan_floatingBlockContent">
                  <Select
                    value={selectedPlanIndex}
                    onChange={(value) => setSelectedPlanIndex(value)}
                    style={{ width: '100%' }}
                  >
                    {pricingPlans2.map((plan, index) => (
                      <Select.Option value={index}>{plan.name}</Select.Option>
                    ))}
                  </Select>
                </div>
              </Section>
            </div>
          )
        }
        {showFloatingBlock && !isMobile && (
          <div
            className="p-pricingPlan_floatingBlock"
            role="region"
            aria-label="Pricing comparison action block"
          >
            <Section className="p-pricingPlan_floatingBlockSection">
              <div ref={compareSectionRef} className="p-pricingPlan_floatingBlockContent">
                <div className="p-pricingPlan_floatingBlockCardFixed" />
                {pricingPlan.map((plan, idx) => (
                  <div className={mapModifiers('p-pricingPlan_floatingBlockCard', plan.highlighted ? 'highlighted' : '')} key={plan.name}>
                    <Typography.Text extendClasses="fs-18x21 p-pricingPlan_floatingBlockCard_planName">{plan.name}</Typography.Text>
                    <div className="p-pricingPlan_priceFloatContainerCompare">
                      {!plan.isFree ? (
                        <Typography.Heading type="h4" fontweight="700" extendClasses="p-pricingPlan_price fs-28x33" className="p-pricingPlan_price">
                          {plan.price !== 0 ? `${plan.price.toLocaleString()}` : 'Let\'s talk'}
                        </Typography.Heading>
                      ) : <Typography.Heading type="h4" fontweight="700" extendClasses="p-pricingPlan_price fs-28x33">{plan.period}</Typography.Heading>}
                      {plan.priceOff !== 0 && (
                        <Typography.Text extendClasses="p-pricingPlan_priceOff">
                          {plan.priceOff}
                        </Typography.Text>
                      )}
                      {!plan.isFree && <Typography.Text extendClasses="p-pricingPlan_period fs-14x18">{plan.period}</Typography.Text>}
                    </div>
                    <div className="p-pricingPlan_buttonContainer">
                      <Button onClick={() => handleOpenModal(plan.name)} extendClasses={mapModifiers('p-pricingPlan_button', plan.highlighted ? 'highlighted' : '')}>{plan.buttonText}</Button>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          </div>
        )}
        <div className="p-pricingPlan_tableCompareHeader"><Typography.Text fontweight="600" extendClasses="fs-24x30">Collaborate and increase productivity</Typography.Text></div>
        {data && data.map((item) => (
          <PricingTable
            table={item}
            plans={pricingPlan}
            key={item.name}
            selectedPlanIndex={selectedPlanIndex}
            hideHeader
            isMobile={isMobile}
          />
        ))}
      </div>
      <div className="p-pricingPlan_sectionMargin" />
      <Section.Title sectionName="Add-ons" className="p-pricingPlan_titleSection">
        Maximize your productivity with add-ons
      </Section.Title>
      <div className={mapModifiers('p-pricingPlan_tabs', activeTabAddOn === 'annually' && 'right')}>
        {['monthly', 'annually'].map((tab) => (
          <Button
            key={tab}
            extendClasses={`p-pricingPlan_tab p-pricingPlan_tab-${activeTabAddOn === tab ? 'active' : ''} ${activeTabAddOn === tab ? 'active' : ''}`}
            onClick={() => setActiveTabAddOn(tab as 'monthly' | 'annually')}
          >
            {tab === 'monthly' && <Typography.Text fontweight="600">Monthly</Typography.Text>}
            {tab === 'annually' && <Typography.Text fontweight="600">Annually</Typography.Text>}
          </Button>
        ))}
      </div>
      {
        isMobile && (
          <div className="p-pricingPlan_addOn_selectFeatures">
            <div className="p-pricingPlan_addOn_selectFeaturesContainer">
              <Select
                value={selectedAddOn}
                onChange={(value) => setSelectedAddOn(value)}
                style={{ width: '100%' }}
                size="large"
              >
                {cardAddOn.map((addOn, index) => (
                  <Select.Option value={addOn.key}>{addOn.title}</Select.Option>
                ))}
              </Select>
              <Typography.Text extendClasses="p-pricingPlan_addOn_description">
                Expand the number of chatbots you can create and manage on the Ask2Auto Portal.
              </Typography.Text>
            </div>
          </div>
        )
      }
      <div className="p-pricingPlan_addOn">
        <div className="p-pricingPlan_addOnContainer">
          {isMobile
            ? (
              <div className="p-pricingPlan_addOnDescriptionContainer">
                {selectedAddOn && cardDescription
                  .filter((item) => item.key === selectedAddOn)
                  .map((item) => (
                    <div className="p-pricingPlan_addOnDescriptionCard" key={item.key}>
                      <Typography.Text className="p-pricingPlan_addOnDescription">{item.title}</Typography.Text>
                      <div className="p-pricingPlan_addOnDescriptionList">
                        {item.description.map((text) => (
                          <div key={text} className="p-pricingPlan_addOnDescriptionItem">
                            <Icon className="p-pricingPlan_addOnIconDescription" iconName="checkLightGreen" size="22" />
                            <Typography.Text extendClasses="fs-14x18">{text}</Typography.Text>
                          </div>
                        ))}
                      </div>
                      <table className="p-pricingPlan_addOnDescriptionTable">
                        <tr>
                          <th className="p-pricingPlan_addOnDescriptionTable_header">
                            <div className="dbox">
                              <div className="dheading_tr">
                                <Typography.Text extendClasses="p-pricingPlan_addOnDescriptionTable_tableHeader fs-14x20">Package</Typography.Text>
                              </div>
                              <div className="dheading_bl">
                                <Typography.Text extendClasses="p-pricingPlan_addOnDescriptionTable_tableHeader fs-14x20">Features</Typography.Text>
                              </div>
                            </div>
                          </th>
                          <th className="p-pricingPlan_addOnDescriptionTable_headerItem">
                            <Typography.Text extendClasses="p-pricingPlan_addOnDescriptionTable_tableHeader fs-14x20">Starter</Typography.Text>
                          </th>
                          <th className="p-pricingPlan_addOnDescriptionTable_headerItem">
                            <Typography.Text extendClasses="p-pricingPlan_addOnDescriptionTable_tableHeader fs-14x20">Plus</Typography.Text>
                          </th>
                          <th className="p-pricingPlan_addOnDescriptionTable_headerItem">
                            <Typography.Text extendClasses="p-pricingPlan_addOnDescriptionTable_tableHeader fs-14x20">Business</Typography.Text>
                          </th>
                        </tr>
                        {selectedAddOn === 'chatbot' && (
                          <tr>
                            <td className="p-pricingPlan_addOnDescriptionTable_headerItem p-pricingPlan_addOnDescriptionTable_tableHeader fs-14x20">Number of chatbot</td>
                            {addOnPackagesChatBot.map((row) => (
                              <td
                                className="p-pricingPlan_addOnDescriptionTable_headerItem"
                                key={row.id}
                              >
                                {row.count}
                              </td>
                            ))}
                          </tr>
                        )}
                        {selectedAddOn === 'knowledge' && (
                          <>
                            <tr>
                              <td className="p-pricingPlan_addOnDescriptionTable_headerItem p-pricingPlan_addOnDescriptionTable_tableHeader fs-14x20">Number of file</td>
                              {addOnPackagesKnowledge.map((row, index) => (
                                <td
                                  key={row.id}
                                  className="p-pricingPlan_addOnDescriptionTable_headerItem"
                                >
                                  {row.count}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="p-pricingPlan_addOnDescriptionTable_headerItem p-pricingPlan_addOnDescriptionTable_tableHeader fs-14x20">Number of web</td>
                              {knowledgeNumberOfWeb.map((row, index) => (
                                <td
                                  key={row.id}
                                  className="p-pricingPlan_addOnDescriptionTable_headerItem"
                                >
                                  {row.count}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="p-pricingPlan_addOnDescriptionTable_headerItem p-pricingPlan_addOnDescriptionTable_tableHeader fs-14x20">MB of storage</td>
                              {mBStorageKnowledge.map((row, index) => (
                                <td
                                  key={row.id}
                                  className="p-pricingPlan_addOnDescriptionTable_headerItem"
                                >
                                  {row.count}
                                </td>
                              ))}
                            </tr>
                          </>
                        )}
                        {selectedAddOn === 'sessions' && (
                          <tr>
                            <td className="p-pricingPlan_addOnDescriptionTable_headerItem p-pricingPlan_addOnDescriptionTable_tableHeader fs-14x20">Number of sessions</td>
                            {addOnPackagesSessions.map((row) => (
                              <td
                                className="p-pricingPlan_addOnDescriptionTable_headerItem"
                                key={row.id}
                              >
                                {row.count}
                              </td>
                            ))}
                          </tr>
                        )}
                        <tr>
                          <td />
                          {selectedAddOn === 'chatbot' && addOnPackagesChatBot.map((row) => (
                            <td key={row.price}>
                              <div className="p-pricingPlan_addOnDescriptionTable_price">
                                <Typography.Text fontweight="600" extendClasses="fs-18x21 p-pricingPlan_addOnDescriptionTable_priceText">{row.price}</Typography.Text>
                                <Typography.Text extendClasses="fs-14x18">
                                  per package/
                                  {isMobile && <br />}
                                  month
                                  {activeTabAddOn === 'annually' && ', billed annually'}
                                </Typography.Text>
                              </div>
                            </td>
                          ))}
                          {selectedAddOn === 'knowledge' && addOnPackagesKnowledge.map((row) => (
                            <td key={row.price}>
                              <div className="p-pricingPlan_addOnDescriptionTable_price">
                                <Typography.Text fontweight="600" extendClasses="fs-18x21 p-pricingPlan_addOnDescriptionTable_priceText">{row.price}</Typography.Text>
                                <Typography.Text extendClasses="fs-14x18">
                                  per package/
                                  {isMobile && <br />}
                                  month
                                  {activeTabAddOn === 'annually' && ', billed annually'}
                                </Typography.Text>
                              </div>
                            </td>
                          ))}
                          {selectedAddOn === 'sessions' && addOnPackagesSessions.map((row) => (
                            <td key={row.price}>
                              <div className="p-pricingPlan_addOnDescriptionTable_price">
                                <Typography.Text fontweight="600" extendClasses="fs-18x21 p-pricingPlan_addOnDescriptionTable_priceText">{row.price}</Typography.Text>
                                <Typography.Text extendClasses="fs-14x18">
                                  per package/
                                  {isMobile && <br />}
                                  month
                                  {activeTabAddOn === 'annually' && ', billed annually'}
                                </Typography.Text>
                              </div>
                            </td>
                          ))}
                        </tr>
                      </table>
                      {/* <div className="p-pricingPlan_priceAddOn">
                        <Typography.Heading type="h2" fontweight="600" extendClasses="p-pricingPlan_price">
                          {item.price.toLocaleString()}
                        </Typography.Heading>
                        <Typography.Text extendClasses="p-pricingPlan_period p-pricingPlan_periodAddOn fs-16x18">{item.period}</Typography.Text>
                      </div> */}
                      {/* {item.info && activeTabAddOn === 'monthly' && (
                        <div className="p-pricingPlan_addOnInfo">
                          <Icon className="p-pricingPlan_addOnIconInfo"
                          iconName="infoGray" size="22" />
                          <Typography.Text extendClasses="p-pricingPlan_addOnInfoText
                           fs-14x18">{item.info}</Typography.Text>
                        </div>
                      )} */}
                    </div>
                  ))}
              </div>
            )
            : cardAddOn.map((item) => (
              // eslint-disable-next-line jsx-a11y/role-supports-aria-props
              <div
                className={mapModifiers('p-pricingPlan_addOnCard', selectedAddOn === item.key && 'selected')}
                role="button"
                aria-selected={selectedAddOn === item.key}
                onClick={() => setSelectedAddOn(item.key)}
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setSelectedAddOn(item.key)}
              >
                <div className="p-pricingPlan_addOnIconContainer">
                  <Icon className="p-pricingPlan_addOnIcon" iconName={item.icon} size="32" />
                </div>
                <div className="p-pricingPlan_addOnContentContainer">
                  <div className="p-pricingPlan_addOnContent">
                    <Typography.Text fontweight="600" extendClasses="fs-18x22">{item.title}</Typography.Text>
                    <Typography.Text extendClasses="fs-14x18">{item.description}</Typography.Text>
                  </div>
                  <Icon className="p-pricingPlan_addOnArrow" iconName={selectedAddOn === item.key ? 'caretRightActive' : 'caretRight'} size="24" />
                </div>
              </div>
            ))}
        </div>
        {!isMobile && (
          <div className="p-pricingPlan_addOnDescriptionContainer">
            {selectedAddOn && cardDescription
              .filter((item) => item.key === selectedAddOn)
              .map((item) => (
                <div className="p-pricingPlan_addOnDescriptionCard" key={item.key}>
                  <Typography.Text className="p-pricingPlan_addOnDescription">{item.title}</Typography.Text>
                  <div className="p-pricingPlan_addOnDescriptionList">
                    {item.description.map((text) => (
                      <div key={text} className="p-pricingPlan_addOnDescriptionItem">
                        <Icon className="p-pricingPlan_addOnIconDescription" iconName="checkLightGreen" size="22" />
                        <Typography.Text extendClasses="fs-14x18">{text}</Typography.Text>
                      </div>
                    ))}
                  </div>
                  <table className="p-pricingPlan_addOnDescriptionTable">
                    <thead className="p-pricingPlan_addOnDescriptionTable_header">
                      <tr>
                        <th className="p-pricingPlan_addOnDescriptionTable_header">
                          <div className="dbox">
                            <div className="dheading_tr">
                              <Typography.Text extendClasses="p-pricingPlan_addOnDescriptionTable_tableHeader">Package</Typography.Text>
                            </div>
                            <div className="dheading_bl">
                              <Typography.Text extendClasses="p-pricingPlan_addOnDescriptionTable_tableHeader">Features</Typography.Text>
                            </div>
                          </div>
                        </th>
                        <th className="p-pricingPlan_addOnDescriptionTable_headerItem">
                          <Typography.Text extendClasses="p-pricingPlan_addOnDescriptionTable_tableHeader">Starter</Typography.Text>
                        </th>
                        <th className="p-pricingPlan_addOnDescriptionTable_headerItem">
                          <Typography.Text extendClasses="p-pricingPlan_addOnDescriptionTable_tableHeader">Plus</Typography.Text>
                        </th>
                        <th className="p-pricingPlan_addOnDescriptionTable_headerItem">
                          <Typography.Text extendClasses="p-pricingPlan_addOnDescriptionTable_tableHeader">Business</Typography.Text>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedAddOn === 'chatbot' && (
                        <tr>
                          <td className="p-pricingPlan_addOnDescriptionTable_headerItem p-pricingPlan_addOnDescriptionTable_tableHeader">Number of chatbot</td>
                          {addOnPackagesChatBot.map((row) => (
                            <td
                              className="p-pricingPlan_addOnDescriptionTable_headerItem"
                              key={row.id}
                            >
                              {row.count}
                            </td>
                          ))}
                        </tr>
                      )}
                      {selectedAddOn === 'knowledge' && (
                        <>
                          <tr>
                            <td className="p-pricingPlan_addOnDescriptionTable_headerItem p-pricingPlan_addOnDescriptionTable_tableHeader">Number of file</td>
                            {addOnPackagesKnowledge.map((row) => (
                              <td
                                className="p-pricingPlan_addOnDescriptionTable_headerItem"
                                key={row.id}
                              >
                                {row.count}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="p-pricingPlan_addOnDescriptionTable_headerItem p-pricingPlan_addOnDescriptionTable_tableHeader">Number of web</td>
                            {knowledgeNumberOfWeb.map((row) => (
                              <td
                                className="p-pricingPlan_addOnDescriptionTable_headerItem"
                                key={row.id}
                              >
                                {row.count}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="p-pricingPlan_addOnDescriptionTable_headerItem p-pricingPlan_addOnDescriptionTable_tableHeader">MB of storage</td>
                            {mBStorageKnowledge.map((row) => (
                              <td
                                className="p-pricingPlan_addOnDescriptionTable_headerItem"
                                key={row.id}
                              >
                                {row.count}
                              </td>
                            ))}
                          </tr>
                        </>
                      )}
                      {selectedAddOn === 'sessions' && (
                        <tr>
                          <td className="p-pricingPlan_addOnDescriptionTable_headerItem p-pricingPlan_addOnDescriptionTable_tableHeader">Number of sessions</td>
                          {addOnPackagesSessions.map((row) => (
                            <td
                              className="p-pricingPlan_addOnDescriptionTable_headerItem"
                              key={row.id}
                            >
                              {row.count}
                            </td>
                          ))}
                        </tr>
                      )}
                      <tr>
                        <td />
                        {selectedAddOn === 'chatbot'
                          && addOnPackagesChatBot.map((row) => (
                            <td key={row.count + row.price}>
                              <div className="p-pricingPlan_addOnDescriptionTable_price">
                                <Typography.Text fontweight="600" extendClasses="fs-18x21 p-pricingPlan_addOnDescriptionTable_priceText">{row.price}</Typography.Text>
                                <Typography.Text extendClasses="fs-14x18">
                                  per package/month
                                  {activeTabAddOn === 'annually' && ', billed annually'}
                                </Typography.Text>
                              </div>
                            </td>
                          ))}
                        {selectedAddOn === 'knowledge'
                          && addOnPackagesKnowledge.map((row) => (
                            <td key={row.count + row.price}>
                              <div className="p-pricingPlan_addOnDescriptionTable_price">
                                <Typography.Text fontweight="600" extendClasses="fs-18x21 p-pricingPlan_addOnDescriptionTable_priceText">{row.price}</Typography.Text>
                                <Typography.Text extendClasses="fs-14x18">
                                  per package/month
                                  {activeTabAddOn === 'annually' && ', billed annually'}
                                </Typography.Text>
                              </div>
                            </td>
                          ))}
                        {selectedAddOn === 'sessions'
                          && addOnPackagesSessions.map((row) => (
                            <td key={row.count + row.price}>
                              <div className="p-pricingPlan_addOnDescriptionTable_price">
                                <Typography.Text fontweight="600" extendClasses="fs-18x21 p-pricingPlan_addOnDescriptionTable_priceText">{row.price}</Typography.Text>
                                <Typography.Text extendClasses="fs-14x18">
                                  per package/month
                                  {activeTabAddOn === 'annually' && ', billed annually'}
                                </Typography.Text>
                              </div>
                            </td>
                          ))}
                      </tr>
                    </tbody>
                  </table>
                  {/* <div className="p-pricingPlan_priceAddOn">
                    <Typography.Heading type="h2" fontweight="600" extendClasses="p-pricingPlan_price">
                      {item.price.toLocaleString()}
                    </Typography.Heading>
                    <Typography.Text extendClasses="p-pricingPlan_period p-pricingPlan_periodAddOn fs-16x18">{item.period}</Typography.Text>
                  </div> */}
                  {/* {item.info && activeTabAddOn === 'monthly' && (
                    <div className="p-pricingPlan_addOnInfo">
                      <Icon className="p-pricingPlan_addOnIconInfo" iconName="infoGray" size="22" />
                      <Typography.Text extendClasses="p-pricingPlan_addOnInfoText
                      fs-14x18">{item.info}</Typography.Text>
                    </div>
                  )} */}
                </div>
              ))}
          </div>
        )}
      </div>
      <div className="p-pricingPlan_sectionMargin" />
      <div className="p-pricingPlan_faq">
        <Section.Title sectionName="FAQ" className="p-pricingPlan_titleSection">
          Frequently asked questions
        </Section.Title>
        <FaqSection list={faqData} categoryName="Pricing" />
      </div>
      <ModalRegister isOpen={isOpen} onClose={() => setIsOpen(false)} />
      <FreeTrial isOpen={isOpenFreeModal} onClose={() => setIsOpenFreeModal(false)} />
    </Section>
  );
};

export default PricingPlan;
